import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { useFirebase } from '#features/api/utils/firebase';
import { getNativeAuthUserId, nativeSessionMark } from '#features/api/utils/nativeAuth';
import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';
import { addPushToken } from '../model/notifications';
import { useNotificationsStore } from '../model/notificationsStore';
import { getPushDeviceSignal } from '../utils/deviceSettings';
import { getPushPlatform } from '../utils/nativePlatform';
import { registerNativePush } from '../utils/nativePush';
import { notifyNativePushChanged } from '../utils/nativePushChanges';
import {
  isNativePushRegistrationCurrent,
  prepareNativePushRegistration,
  registerNativePushToken,
} from '../utils/nativePushRegistration';
import { waitForServiceWorker } from '../utils/waitForServiceWorker';

export const usePushNotifications = () => {
  const firebaseClient = useFirebase();
  const notificationsStore = useNotificationsStore();
  const [nativeEnabling, setNativeEnabling] = useState(false);
  const queryClient = useQueryClient();
  const addPushTokenMutation = useMutation({
    // Тип не перечисляется руками: снимок устройства растёт, и список, продублированный в трёх
    // местах, разъезжается на первой же правке.
    mutationFn: (input: Parameters<typeof addPushToken>[0]) => addPushToken(input),
  });

  const enableNotifications = useCallback(async () => {
    // if (notificationsStore.status === 'ENABLED') {
    //   return;
    // }

    // Native shell: register via the Capacitor plugin (not the web SW) and tag the
    // token with the real platform — ANDROID → FCM notification, IOS → direct APNs —
    // so the backend delivers an OS-rendered notification even when the app is closed.
    if (envConfig.IS_NATIVE) {
      const credential = await nativeSessionMark();
      const ownerId = await getNativeAuthUserId();
      if (!ownerId) return;
      const context = prepareNativePushRegistration(ownerId, credential);
      if (!context) return;
      setNativeEnabling(true);
      try {
        const token = await registerNativePush();
        if (!isNativePushRegistrationCurrent(context)) return;
        if (!token) {
          notificationsStore.setStatus('DISABLED');
          return;
        }
        try {
          const platform = await getPushPlatform();
          const device = await getPushDeviceSignal();
          const data = await registerNativePushToken(context, {
            input: { token, platform, ...device },
            onRegistered: () => {
              localStorage.setItem('fcm_token', token);
              notificationsStore.setStatus('ENABLED');
              queryClient.invalidateQueries({ queryKey: ['me'] });
            },
          });
          if (
            isNativePushRegistrationCurrent(context) &&
            !(data?.__typename === 'Operation' && data.status === 'OK')
          ) {
            console.warn('[push] native addPushToken did not confirm registration', data);
          }
        } catch (err) {
          console.warn('[push] native addPushToken mutation failed', err);
        }
      } finally {
        setNativeEnabling(false);
        notifyNativePushChanged();
      }
      return;
    }

    const registerNotifications = async () => {
      if (!firebaseClient) {
        console.warn('[push] firebase client unavailable — push not enabled');
        notificationsStore.setStatus('DISABLED');
        return;
      }

      // Ждём живого воркера, и только теперь, когда разрешение уже получено. Почему это
      // обязательно и почему наперегонки с таймером - в самом помощнике.
      await waitForServiceWorker();

      let token: string | null = null;
      try {
        token = await firebaseClient.getPushToken();
      } catch (err) {
        console.warn('[push] getPushToken threw', err);
      }

      if (!token) {
        console.warn('[push] getPushToken returned no token (FCM/SW init failed?)');
        notificationsStore.setStatus('DISABLED');
        return;
      }

      try {
        const data = await addPushTokenMutation.mutateAsync({ token, platform: 'WEB' });
        if (data?.__typename === 'Operation' && data.status === 'OK') {
          localStorage.setItem('fcm_token', token);
          notificationsStore.setStatus('ENABLED');
          // Refresh `useProfile()` so consumers (e.g. BrowserPushStatus banner)
          // re-render once the new token is reflected in profile.pushToken[].
          queryClient.invalidateQueries({ queryKey: ['me'] });
        } else {
          console.warn('[push] addPushToken did not confirm registration', data);
        }
      } catch (err) {
        console.warn('[push] addPushToken mutation failed', err);
      }
    };

    if (!('Notification' in window)) {
      console.log('Notifications are not supported');
    } else if (Notification.permission === 'granted') {
      console.log('Permission already granted');
      registerNotifications();
    } else if (Notification.permission !== 'denied') {
      /*
       * ⚠️ Между кликом человека и этой строкой нельзя ждать НИЧЕГО.
       *
       * Firefox и Safari поднимают диалог только по «свежему» жесту пользователя: любой await
       * перед этим вызовом, и обещание спокойно вернёт 'default', без диалога и без ошибки, а
       * Chrome при этом продолжит работать как ни в чём не бывало. Единственный след такой
       * поломки это метка 'default' в событии web_push_dialog_outcome.
       *
       * Ожидание service worker живёт ниже, в registerNotifications, уже после ответа.
       */
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        registerNotifications();
      }
    }
  }, [addPushTokenMutation, firebaseClient, notificationsStore, queryClient]);

  const disableNotifications = useCallback(async () => {
    if (firebaseClient) {
      await firebaseClient.deletePushToken();
    }
    notificationsStore.setStatus('DISABLED');
  }, [firebaseClient, notificationsStore]);

  return {
    enableNotifications,
    disableNotifications,
    status: notificationsStore.status,
    isEnabling: nativeEnabling || addPushTokenMutation.isPending,
  };
};
